/**
 * Pinia Store 重置插件
 *
 * 功能：
 * 1. 为所有 store 自动添加 $resetAll 方法
 * 2. 自动清除 persist 插件保存的数据（支持白名单和配置项）
 * 3. 重置 store 状态到初始值
 *
 * 使用方式：
 * 1. 全局白名单：在 STORAGE_WHITE_LIST 中添加需要保护的 key
 * 2. Store 级别：在 persist 配置中添加 skipOnReset: true
 *    persist: {
 *      key: 'my-store',
 *      skipOnReset: true  // 这个 store 的数据在重置时不会被删除
 *    }
 */

import type { PiniaPluginContext } from 'pinia';
import { STORAGE_WHITE_LIST } from '@/utils/storage';

/**
 * 扩展 persist 配置类型
 */
interface PersistConfig {
  key?: string;
  storage?: Storage;
  skipOnReset?: boolean;  // 是否跳过重置（不删除此 store 的持久化数据）
  [key: string]: any;
}

export function createResetPlugin() {
  return ({ store, options }: PiniaPluginContext) => {
    // 保存初始状态（深拷贝）
    const initialState = JSON.parse(JSON.stringify(store.$state));

    /**
     * 重置单个 store 的所有状态和持久化数据
     */
    store.$resetAll = () => {
      // 1. 重置状态到初始值
      store.$patch(initialState);

      // 2. 如果有 persist 配置，清除对应的 storage
      if (options.persist) {
        const persistConfig = Array.isArray(options.persist)
          ? options.persist
          : [options.persist];

        persistConfig.forEach((configItem: any) => {
          // persist 可能是 true 或者配置对象，需要规范化
          const config: PersistConfig = configItem === true ? {} : configItem;

          const key = config.key || store.$id;
          const storage = config.storage || localStorage;

          // 方式1：检查 persist 配置中的 skipOnReset 选项
          if (config.skipOnReset === true) {
            // console.log(`[resetPlugin] 跳过重置 (skipOnReset=true): ${key}`);

            return;
          }

          // 方式2：检查是否在全局白名单中
          if (STORAGE_WHITE_LIST.includes(key)) {
            // console.log(`[resetPlugin] 保留受保护的 key (白名单): ${key}`);

            return;
          }

          // 删除持久化的 key
          // console.log(`[resetPlugin] 删除 storage key: ${key}`);
          storage.removeItem(key);
        });
      }
    };
  };
}

/**
 * 扩展 Pinia Store 类型
 */
declare module 'pinia' {
  export interface PiniaCustomProperties {
    /**
     * 重置 store 状态并清除持久化数据
     */
    $resetAll: () => void;
  }
}
